import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import type { ChangeOrder, ProjectPLBucket } from '../../types';
import { calculateTotals } from '../../utils/proposalCalculations';
import { formatBondingPercent } from '../../utils/proposalBonding';

const BUCKET_LABELS: Record<ProjectPLBucket, string> = {
  design: 'Design',
  engineering: 'Engineering',
  timber: 'Timber / Materials',
  production: 'Production',
  freight: 'Freight',
  install: 'Install',
  travel: 'Travel',
  overhead: 'Overhead',
  unassigned: 'Other',
};

const styles = StyleSheet.create({
  page: { padding: 44, fontFamily: 'Helvetica', fontSize: 10, color: '#1a1a1a', backgroundColor: '#ffffff' },
  // Logo sits on the dark banner so the light/cream brand mark stays visible.
  logoOnDark: { width: 150, height: 'auto', alignSelf: 'center', marginBottom: 16 },
  banner: { backgroundColor: '#2C3E2F', padding: 18, borderRadius: 8, marginBottom: 22 },
  bannerEyebrow: { color: 'rgba(255,255,255,0.7)', fontSize: 10, letterSpacing: 1, marginBottom: 4 },
  bannerTitle: { color: '#ffffff', fontSize: 18, fontWeight: 700 },
  metaRow: { flexDirection: 'row', marginBottom: 6 },
  metaLabel: { fontSize: 10, color: '#64748b', width: 110 },
  metaValue: { fontSize: 10, color: '#0f172a', fontWeight: 600 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#2C3E2F', marginTop: 18, marginBottom: 8 },
  scopeBox: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: 12, marginBottom: 8 },
  scopeText: { fontSize: 10, color: '#334155', lineHeight: 1.5 },
  tableHead: { flexDirection: 'row', borderBottom: '1px solid #cbd5e1', paddingBottom: 6, marginBottom: 4 },
  th: { fontSize: 9, color: '#64748b', fontWeight: 700 },
  row: { flexDirection: 'row', paddingVertical: 5, borderBottom: '1px solid #f1f5f9' },
  td: { fontSize: 10, color: '#0f172a' },
  colDesc: { width: '50%' },
  colCat: { width: '22%' },
  colQty: { width: '10%', textAlign: 'right' },
  colAmt: { width: '18%', textAlign: 'right' },
  totalsBox: { marginTop: 16, alignSelf: 'flex-end', width: 260 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  totalLabel: { fontSize: 10, color: '#475569' },
  totalValue: { fontSize: 10, color: '#0f172a', fontWeight: 600 },
  grandRow: { flexDirection: 'row', justifyContent: 'space-between', paddingTop: 8, marginTop: 6, borderTop: '2px solid #2C3E2F' },
  grandLabel: { fontSize: 12, color: '#2C3E2F', fontWeight: 700 },
  grandValue: { fontSize: 14, color: '#2C3E2F', fontWeight: 700 },
  sigBanner: { backgroundColor: '#2C3E2F', padding: 18, borderRadius: 8, marginBottom: 26, alignItems: 'center' },
  sigWrap: { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, padding: 14, alignItems: 'center', marginBottom: 20 },
  legal: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: 12, borderRadius: 4 },
  footer: { position: 'absolute', bottom: 28, left: 44, right: 44, borderTop: '1px solid #e2e8f0', paddingTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 8, color: '#94a3b8' },
});

const fmt = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

function coNumber(n: number): string {
  return `CO-${String(Math.max(1, Math.floor(n || 1))).padStart(3, '0')}`;
}

function coReference(co: ChangeOrder): string {
  if (co.reference) return co.reference;
  const pn = (co.projectNumber || '').trim();
  return pn ? `${pn}-${coNumber(co.number)}` : coNumber(co.number);
}

export interface ChangeOrderPdfProps {
  changeOrder: ChangeOrder;
  companyLogo?: string | null;
  companyName?: string;
  companyWebsite?: string;
  priorContractValue: number;
  generatedDate?: Date;
}

export default function ChangeOrderPdfDocument({
  changeOrder: co,
  companyLogo,
  companyName = "Leiper's Creek Timber Frames",
  companyWebsite = 'www.lctimberframes.com',
  priorContractValue: priorContractValueProp,
  generatedDate = new Date(),
}: ChangeOrderPdfProps) {
  // The approval snapshot wins: later COs must never move what this one showed.
  const priorContractValue =
    typeof co.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)
      ? co.contractValueBefore
      : priorContractValueProp;
  const newContract = priorContractValue + co.deltaAmount;

  const rows: { desc: string; cat: string; qty: number; amount: number }[] = [];
  if (co.pricingMode === 'line_item' && co.lineItems) {
    co.lineItems.forEach(li =>
      rows.push({ desc: li.description || 'Item', cat: BUCKET_LABELS[li.bucket] || 'Other', qty: li.quantity || 1, amount: li.amount }),
    );
  } else if (co.pricingMode === 'builder' && co.builderNodeSets) {
    const margin = co.builderProfitMargin ?? 0;
    co.builderNodeSets
      .filter(ns => ns.enabled !== false)
      .forEach(ns => {
        const totals = calculateTotals([ns], margin);
        rows.push({ desc: ns.name || 'Structure', cat: 'Structure', qty: ns.quantity || 1, amount: totals.billableTotal });
      });
  } else {
    rows.push({ desc: co.title || 'Change order', cat: 'Lump sum', qty: 1, amount: co.deltaAmount - (co.bondingAdjustment || 0) });
  }
  // Bonding pass-through rides on the scope change; absent on COs priced without bonding.
  const bondingAdjustment = Number(co.bondingAdjustment) || 0;
  if (bondingAdjustment !== 0) {
    rows.push({
      desc: `Bonding adjustment (${formatBondingPercent(Number(co.bondingPercent) || 0)} of scope change)`,
      cat: 'Bonding',
      qty: 1,
      amount: bondingAdjustment,
    });
  }

  const signedDate = (() => {
    const s = co.signature?.signedAt;
    if (!s) return null;
    const d = s instanceof Date ? s : (s as unknown as { toDate?: () => Date }).toDate?.();
    return d || null;
  })();

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.banner}>
          {companyLogo && <Image src={companyLogo} style={styles.logoOnDark} />}
          <Text style={styles.bannerEyebrow}>CHANGE ORDER {coReference(co)}</Text>
          <Text style={styles.bannerTitle}>{co.title}</Text>
        </View>

        <View style={styles.metaRow}>
          <Text style={styles.metaLabel}>Project:</Text>
          <Text style={styles.metaValue}>{co.projectName || co.clientName || '—'}</Text>
        </View>
        {co.projectNumber && (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Project #:</Text>
            <Text style={styles.metaValue}>{co.projectNumber}</Text>
          </View>
        )}
        <View style={styles.metaRow}>
          <Text style={styles.metaLabel}>Client:</Text>
          <Text style={styles.metaValue}>{co.clientName || '—'}</Text>
        </View>
        <View style={styles.metaRow}>
          <Text style={styles.metaLabel}>Date:</Text>
          <Text style={styles.metaValue}>{generatedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</Text>
        </View>

        {co.scopeDescription && (
          <>
            <Text style={styles.sectionTitle}>Scope of Work</Text>
            <View style={styles.scopeBox}>
              <Text style={styles.scopeText}>{co.scopeDescription}</Text>
            </View>
          </>
        )}

        <Text style={styles.sectionTitle}>Pricing</Text>
        <View style={styles.tableHead}>
          <Text style={[styles.th, styles.colDesc]}>Description</Text>
          <Text style={[styles.th, styles.colCat]}>Category</Text>
          <Text style={[styles.th, styles.colQty]}>Qty</Text>
          <Text style={[styles.th, styles.colAmt]}>Amount</Text>
        </View>
        {rows.map((r, i) => (
          <View style={styles.row} key={i}>
            <Text style={[styles.td, styles.colDesc]}>{r.desc}</Text>
            <Text style={[styles.td, styles.colCat]}>{r.cat}</Text>
            <Text style={[styles.td, styles.colQty]}>{r.qty}</Text>
            <Text style={[styles.td, styles.colAmt]}>{fmt(r.amount)}</Text>
          </View>
        ))}

        <View style={styles.totalsBox}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Prior contract value</Text>
            <Text style={styles.totalValue}>{fmt(priorContractValue)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>This change order</Text>
            <Text style={styles.totalValue}>{co.deltaAmount >= 0 ? '+' : ''}{fmt(co.deltaAmount)}</Text>
          </View>
          <View style={styles.grandRow}>
            <Text style={styles.grandLabel}>New contract value</Text>
            <Text style={styles.grandValue}>{fmt(newContract)}</Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{companyName} • {companyWebsite} • {coReference(co)}</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>

      {co.signature ? (
        <Page size="LETTER" style={styles.page}>
          <View style={styles.sigBanner}>
            {companyLogo && <Image src={companyLogo} style={styles.logoOnDark} />}
            <Text style={{ color: '#ffffff', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Change Order Accepted</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>
              This change order has been reviewed and accepted by the client.
            </Text>
          </View>

          <Text style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 10 }}>Client Signature</Text>
          <View style={styles.sigWrap}>
            {co.signature.dataUrl && (
              <Image src={co.signature.dataUrl} style={{ width: 200, height: 80, objectFit: 'contain', marginBottom: 10 }} />
            )}
            <View style={{ width: '100%', borderTop: '1px solid #334155', paddingTop: 8, marginTop: 5 }}>
              <Text style={{ fontSize: 11, color: '#334155', textAlign: 'center' }}>{co.signature.signedBy}</Text>
            </View>
          </View>

          <View style={{ backgroundColor: '#f8fafc', padding: 14, borderRadius: 4, marginBottom: 22 }}>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Signed By:</Text>
              <Text style={styles.metaValue}>{co.signature.signedBy}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date Signed:</Text>
              <Text style={styles.metaValue}>
                {signedDate
                  ? signedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Change Amount:</Text>
              <Text style={styles.metaValue}>{co.deltaAmount >= 0 ? '+' : ''}{fmt(co.deltaAmount)}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>New Contract:</Text>
              <Text style={styles.metaValue}>{fmt(newContract)}</Text>
            </View>
          </View>

          <View style={styles.legal}>
            <Text style={{ fontSize: 9, color: '#166534', lineHeight: 1.4 }}>
              By signing this change order, the client authorizes the described change in scope and the corresponding adjustment to the
              contract value. This signed copy serves as the official record of agreement between the parties and is incorporated into the
              original signed proposal.
            </Text>
          </View>

          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>{companyName} • {companyWebsite} • Signed {coReference(co)}</Text>
            <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      ) : co.internalApproval ? (
        <Page size="LETTER" style={styles.page}>
          <View style={styles.sigBanner}>
            {companyLogo && <Image src={companyLogo} style={styles.logoOnDark} />}
            <Text style={{ color: '#ffffff', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Change Order Approved</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10 }}>
              Client approval recorded internally — no portal signature on file.
            </Text>
          </View>

          <View style={{ backgroundColor: '#f8fafc', padding: 14, borderRadius: 4, marginBottom: 22 }}>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Recorded By:</Text>
              <Text style={styles.metaValue}>{co.internalApprovedBy || 'Staff'}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date Approved:</Text>
              <Text style={styles.metaValue}>
                {(() => {
                  const a = co.approvedAt;
                  if (!a) return '—';
                  const d = a instanceof Date ? a : (a as unknown as { toDate?: () => Date }).toDate?.();
                  return d
                    ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '—';
                })()}
              </Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Change Amount:</Text>
              <Text style={styles.metaValue}>{co.deltaAmount >= 0 ? '+' : ''}{fmt(co.deltaAmount)}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>New Contract:</Text>
              <Text style={styles.metaValue}>{fmt(newContract)}</Text>
            </View>
          </View>

          <View style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', padding: 12, borderRadius: 4 }}>
            <Text style={{ fontSize: 9, color: '#92400e', lineHeight: 1.4 }}>
              This change order was approved on the client&apos;s behalf by Leiper&apos;s Creek staff based on offline agreement.
              The preferred approval method is client signature through the portal signing link.
            </Text>
          </View>

          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>{companyName} • {companyWebsite} • {coReference(co)}</Text>
            <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      ) : null}
    </Document>
  );
}
